import { useDebounceFn, useEventListener } from "@vueuse/core";
import { onScopeDispose, type Ref, watch } from "vue";
import { z } from "zod";

import type { UsePlayerReturn } from "@/composables/use-player.composable";
import { useSessionStore } from "@/stores/session.store";
import type { Product, Track } from "@/types";

const STORAGE_KEY = "player-resume-v1";

const checkpointSchema = z
  .object({
    userId: z.string().min(1),
    productId: z.string().min(1),
    trackId: z.string().min(1),
    positionSeconds: z.number().finite().nonnegative(),
  })
  .strict();

type Checkpoint = z.infer<typeof checkpointSchema>;

function writeCheckpoint(checkpoint: Checkpoint) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(checkpoint));
  } catch {
    // Resume persistence is optional. Playback must continue without it.
  }
}

function readCheckpoint(): Checkpoint | undefined {
  try {
    const storedCheckpoint = localStorage.getItem(STORAGE_KEY);

    if (!storedCheckpoint) {
      return undefined;
    }

    const parsedCheckpoint: unknown = JSON.parse(storedCheckpoint);
    const result = checkpointSchema.safeParse(parsedCheckpoint);

    return result.success ? result.data : undefined;
  } catch {
    return undefined;
  }
}

function clearCheckpoint() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Resume persistence is optional. Playback must continue without it.
  }
}

export function usePlayerResume(
  player: UsePlayerReturn,
  product: Ref<Product | undefined>,
) {
  const session = useSessionStore();
  const persist = useDebounceFn(writeCheckpoint, 1_000, {
    maxWait: 1_000,
  });

  function queue(track: Track | undefined, seconds: number) {
    const currentProduct = product.value;
    const userId = session.user?.id;

    if (
      !userId ||
      !currentProduct?.canPlay ||
      !track ||
      !currentProduct.tracks.some(
        (productTrack) => productTrack.id === track.id,
      )
    ) {
      return;
    }

    void persist({
      userId,
      productId: currentProduct.id,
      trackId: track.id,
      positionSeconds: seconds,
    });
  }

  function flush() {
    if (!player.ended.value) {
      queue(player.currentTrack.value, player.timeElapsed.value ?? 0);
    }

    persist.flush();
  }

  watch(
    player.ended,
    (ended) => {
      if (!ended) {
        return;
      }

      persist.cancel();

      const track =
        player.options.value.repeatMode === "track"
          ? player.currentTrack.value
          : player.nextTrack.value;

      if (!track) {
        clearCheckpoint();
        return;
      }

      queue(track, 0);
      persist.flush();
    },
    { flush: "sync" },
  );
  watch(
    [player.currentTrack, player.timeElapsed],
    ([track, seconds]) => {
      if (!player.ended.value) {
        queue(track, seconds ?? 0);
      }
    },
    { flush: "sync" },
  );
  watch(
    player.isPlaybackRequested,
    (isPlaybackRequested, wasPlaybackRequested) => {
      if (wasPlaybackRequested && !isPlaybackRequested) {
        flush();
      }
    },
    { flush: "sync" },
  );

  useEventListener(document, "visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      flush();
    }
  });
  useEventListener(window, "pagehide", flush);
  onScopeDispose(() => persist.flush(), true);

  function restore(products: readonly Product[] | undefined) {
    if (!products || product.value || player.tracks.value.length) {
      return;
    }

    const checkpoint = readCheckpoint();
    const userId = session.user?.id;

    if (!checkpoint || !userId || checkpoint.userId !== userId) {
      return;
    }

    const savedProduct = products.find(
      (candidate) => candidate.id === checkpoint.productId,
    );
    const initialIndex = savedProduct?.tracks.findIndex(
      (track) => track.id === checkpoint.trackId,
    );

    if (
      !savedProduct?.canPlay ||
      initialIndex === undefined ||
      initialIndex < 0
    ) {
      clearCheckpoint();
      return;
    }

    product.value = savedProduct;
    player.setTracklist(savedProduct.tracks, {
      initialIndex,
      startAtSeconds: checkpoint.positionSeconds,
    });
  }

  function clear() {
    persist.cancel();
    clearCheckpoint();
  }

  return { clear, restore };
}
